import { setupLayouts } from "virtual:meta-layouts";
import type { App } from "vue";

import type { RouteRecordRaw } from "vue-router";

import { routes as accountRoutes } from "@/kibi/modules/account/routes";
import { routes as auditLogRoutes } from "@/kibi/modules/auditlog/routes";
import { routes as authRoutes } from "@/kibi/modules/auth/routes";
import { routes as dashboardRoutes } from "@/kibi/modules/dashboard/routes";
import { routes as emailRoutes } from "@/kibi/modules/email/routes";
import { organizationRoutes } from "@/kibi/modules/organization/routes";
import { routes as productRoutes } from "@/kibi/modules/product/routes";
import { routes as profileRoutes } from "@/kibi/modules/profile/routes";
import { routes as templateRoutes } from "@/kibi/modules/template/routes";
import { createRouter, createWebHistory } from "vue-router";
import { authGuard } from "./guards/auth";

/**
 * Recursively wraps leaf routes in their assigned layout component.
 *
 * This function traverses the route tree. If a route has children, it recurses.
 * If a route is a "leaf" (has no children), it passes it to `setupLayouts` (from `vite-plugin-vue-meta-layouts`).
 * `setupLayouts` reads the `layout` property from the route's `meta` object and wraps the route component
 * inside the corresponding layout component (e.g., `layouts/blank.vue` or `layouts/default.vue`).
 *
 * This ensures that when you define `meta: { layout: 'blank' }` in a page, it is actually rendered individually
 * inside that layout structure.
 */
function recursiveLayouts(route: RouteRecordRaw): RouteRecordRaw {
  if (route.children) {
    for (let i = 0; i < route.children.length; i++)
      route.children[i] = recursiveLayouts(route.children[i]);

    return route;
  }

  return setupLayouts([route])[0];
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash, behavior: "smooth", top: 60 };

    return { top: 0 };
  },
  routes: [
    ...dashboardRoutes.map((route) => recursiveLayouts(route)),
    ...authRoutes.map((route) => recursiveLayouts(route)),
    ...profileRoutes.map((route) => recursiveLayouts(route)),
    ...accountRoutes.map((route) => recursiveLayouts(route)),
    ...organizationRoutes.map((route) => recursiveLayouts(route)),
    ...productRoutes.map((route) => recursiveLayouts(route)),
    ...auditLogRoutes.map((route) => recursiveLayouts(route)),
    ...templateRoutes.map((route) => recursiveLayouts(route)),
    ...emailRoutes.map((route) => recursiveLayouts(route)),
    {
      path: "/:pathMatch(.*)*",
      redirect: (to) => ({
        path: "/auth/acess-denied",
        query: { error: "route_not_found", path: to.fullPath },
      }),
    },
  ],
});

// Register authentication guard
router.beforeEach(authGuard);

export { router };

export default function (app: App) {
  app.use(router);
}
